import React from 'react';
import {
  Flag,
  FileText,
  ChartNoAxesCombined,
  Mail,
  MessageSquareWarning,
  Package,
  Settings2,
  ShieldAlert,
  UserRoundX,
  Users,
} from 'lucide-react';
import { useTranslations } from '../translations';
import './AdminSectionSidebar.css';

type AdminSectionItem =
  | 'statistics'
  | 'users'
  | 'products'
  | 'reported-products'
  | 'reported-users'
  | 'reported-chats'
  | 'contact-messages'
  | 'illegal-content-notices'
  | 'legal-documents'
  | 'service-notices'
  | 'configuration';

interface AdminSectionSidebarProps {
  activeItem: AdminSectionItem;
  onStatisticsClick?: () => void;
  onUsersClick?: () => void;
  onProductsClick?: () => void;
  onReportedProductsClick?: () => void;
  onReportedUsersClick?: () => void;
  onReportedChatsClick?: () => void;
  onContactMessagesClick?: () => void;
  onIllegalContentNoticesClick?: () => void;
  onLegalDocumentsClick?: () => void;
  onServiceNoticesClick?: () => void;
  onConfigurationClick?: () => void;
}

interface NavigationItem {
  id: AdminSectionItem;
  label: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  onClick?: () => void;
}

const AdminSectionSidebar: React.FC<AdminSectionSidebarProps> = ({
  activeItem,
  onStatisticsClick,
  onUsersClick,
  onProductsClick,
  onReportedProductsClick,
  onReportedUsersClick,
  onReportedChatsClick,
  onContactMessagesClick,
  onIllegalContentNoticesClick,
  onLegalDocumentsClick,
  onServiceNoticesClick,
  onConfigurationClick,
}) => {
  const t = useTranslations();

  const navigationItems: NavigationItem[] = [
    {
      id: 'statistics',
      label: t.administration.statisticsTab,
      icon: ChartNoAxesCombined,
      onClick: onStatisticsClick,
    },
    {
      id: 'users',
      label: t.administration.usersTab,
      icon: Users,
      onClick: onUsersClick,
    },
    {
      id: 'products',
      label: t.administration.productsTab,
      icon: Package,
      onClick: onProductsClick,
    },
    {
      id: 'reported-products',
      label: t.administration.reportedProductsTab,
      icon: Flag,
      onClick: onReportedProductsClick,
    },
    {
      id: 'reported-users',
      label: t.administration.reportedUsersTab,
      icon: UserRoundX,
      onClick: onReportedUsersClick,
    },
    {
      id: 'reported-chats',
      label: t.administration.reportedChatsTab,
      icon: MessageSquareWarning,
      onClick: onReportedChatsClick,
    },
    {
      id: 'contact-messages',
      label: t.administration.contactMessagesTab,
      icon: Mail,
      onClick: onContactMessagesClick,
    },
    {
      id: 'illegal-content-notices',
      label: t.administration.illegalContentTab,
      icon: ShieldAlert,
      onClick: onIllegalContentNoticesClick,
    },
    {
      id: 'legal-documents',
      label: t.administration.legalDocumentsTab,
      icon: FileText,
      onClick: onLegalDocumentsClick,
    },
    {
      id: 'service-notices',
      label: t.administration.serviceNoticesTab,
      icon: Mail,
      onClick: onServiceNoticesClick,
    },
    {
      id: 'configuration',
      label: t.administration.configurationTab,
      icon: Settings2,
      onClick: onConfigurationClick,
    },
  ];

  return (
    <nav className="admin-section-sidebar" aria-label={t.administration.title}>
      {navigationItems.map(({ id, label, icon: Icon, onClick }) => {
        const isActive = activeItem === id;

        return (
          <button
            key={id}
            type="button"
            className={`admin-section-sidebar-item ${isActive ? 'active' : ''}`}
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon className="admin-section-sidebar-icon" aria-hidden />
            <span className="admin-section-sidebar-label">{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default AdminSectionSidebar;
